import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Modal from "./Modal";
import { formatIDR } from "../lib/format";
import { CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { FormError } from "./ui/feedback";

export default function BulkEditDialog({ open, onClose, onUpdated }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    setError("");
    try {
      const res = await api.get("/items/bulk-edit-template/xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "bulk_edit_template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const submitFile = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/items/bulk-edit-preview", fd);
      setPreview(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const applyAll = async () => {
    setLoading(true);
    setError("");
    try {
      const body = {
        rows: preview.rows.map((r) => ({
          part_number: r.part_number,
          ...r.new,
          expected_item_id: r.expected_item_id,
          expected_updated_at: r.expected_updated_at,
        })),
      };
      const { data } = await api.post("/items/bulk-edit-apply", body);
      toast.success(`Bulk edit complete — ${data.updated} items updated`);
      onUpdated?.();
      close();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Modal title="Bulk Edit Items" onClose={close} busy={loading} testid="bulk-edit-dialog" size="lg">
      <FormError>{error}</FormError>
      {!preview ? (
        <div className="space-y-5">
          <p className="text-sm text-zinc-600">
            Upload an Excel (.xlsx) file to bulk edit existing items. Only rows matching an
            existing part number will be updated. Stock fields are never changed.
          </p>
          <Button type="button" variant="export"
            onClick={downloadTemplate}
          >
            ↓ Download Bulk Edit template
          </Button>
          <div>
            <div className="text-[10px] tracking-[0.1em] uppercase font-medium text-zinc-700 mb-1.5">
              Excel file (.xlsx)
            </div>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-zinc-600 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary-hover cursor-pointer"
            />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-zinc-200">
            <Button type="button" variant="cancel"
              onClick={close}
            >
              Cancel
            </Button>
            <Button type="button" variant="review"
              onClick={submitFile}
              disabled={!file}
              loading={loading}
              loadingLabel="Parsing…"
            >
              Preview →
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-semantic-success border border-success/30 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-semantic-success font-medium">
                Will Update
              </div>
              <div className="text-xl font-semibold tabular-nums text-semantic-success">{preview.matched}</div>
            </div>
            <div className="bg-semantic-warning border border-warning/30 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-semantic-warning font-medium">
                Not Found (Skipped)
              </div>
              <div className="text-xl font-semibold tabular-nums text-semantic-warning">{preview.skipped}</div>
            </div>
            <div className="bg-semantic-info border border-info/30 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-semantic-info font-medium">
                Total
              </div>
              <div className="text-xl font-semibold tabular-nums text-semantic-info">{preview.total}</div>
            </div>
          </div>

          <div className="border border-zinc-200 rounded-sm max-h-[400px] overflow-auto">
            <table className="wms-table">
              <thead className="sticky top-0 bg-zinc-50 z-10">
                <tr>
                  <th>Part #</th>
                  <th>Model</th>
                  <th>Part Name</th>
                  <th>Description</th>
                  <th>Price</th>
                  <th>Rack</th>
                  <th>Threshold</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="font-mono text-xs font-medium">{r.part_number}</td>
                    <td className="text-xs">
                      <div>{r.new.model || "—"}</div>
                      {r.old.model !== r.new.model && (
                        <div className="text-[10px] text-zinc-400">was: {r.old.model || "—"}</div>
                      )}
                    </td>
                    <td className="text-xs">
                      <div>{r.new.part_name || "—"}</div>
                      {r.old.part_name !== r.new.part_name && (
                        <div className="text-[10px] text-zinc-400">was: {r.old.part_name || "—"}</div>
                      )}
                    </td>
                    <td className="text-xs">
                      <div>{r.new.description || "—"}</div>
                      {r.old.description !== r.new.description && (
                        <div className="text-[10px] text-zinc-400">was: {r.old.description || "—"}</div>
                      )}
                    </td>
                    <td className="num text-xs">
                      <div>{formatIDR(r.new.price)}</div>
                      {r.old.price !== r.new.price && (
                        <div className="text-[10px] text-zinc-400">was: {formatIDR(r.old.price)}</div>
                      )}
                    </td>
                    <td className="text-xs">
                      <div>{r.new.rack_location || "—"}</div>
                      {r.old.rack_location !== r.new.rack_location && (
                        <div className="text-[10px] text-zinc-400">was: {r.old.rack_location || "—"}</div>
                      )}
                    </td>
                    <td className="num text-xs">
                      <div>{r.new.low_stock_threshold}</div>
                      {r.old.low_stock_threshold !== r.new.low_stock_threshold && (
                        <div className="text-[10px] text-zinc-400">was: {r.old.low_stock_threshold}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-zinc-200">
              <Button type="button" variant="navigation-back"
              onClick={() => { setPreview(null); setFile(null); }}
            >
              ← Back
            </Button>
            <Button type="button" variant="success"
              onClick={applyAll}
              disabled={preview.matched === 0}
              loading={loading}
              loadingLabel="Updating…"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {`Update ${preview.matched} items`}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
